// working with dashboard , but i am going to setup institution 

// src/router/AppRouter.tsx
// Application router — defines all routes with appropriate guards and layout.
import { createBrowserRouter, Navigate } from 'react-router-dom'
import { lazy, Suspense } from 'react'
import Login            from '@/pages/auth/Login'
import ForgotPassword   from '@/pages/auth/ForgotPassword'
import UnauthorizedPage from '@/pages/UnauthorizedPage'
import AuthGuard        from './guards/AuthGuard'
import SuperAdminGuard  from './guards/SuperAdminGuard'
import PermissionGuard  from './guards/PermissionGuard'
import SuperAdminLayout from '@/layouts/SuperAdminLayout'
import DashboardSkeleton from '@/pages/dashboard/components/DashboardSkeleton'
import TablePageSkeleton from '@/components/TablePageSkeleton'
import ProfileSkeleton   from '@/components/ProfileSkeleton'

const DashboardContainer = lazy(() => import('@/pages/dashboard/components/DashboardContainer'))
const InstitutionsPage = lazy(() => import('@/pages/institutions'))
const StaffPage = lazy(() => import('@/pages/staff/StaffPage'))
const CoursesPage = lazy(() => import('@/pages/CoursesPage'))
const StudentsPage = lazy(() => import('@/pages/StudentsPage'))
const PaymentsPage = lazy(() => import('@/pages/PaymentsPage'))
const CommissionsPage = lazy(() => import('@/pages/CommissionsPage'))
const CouponsPage = lazy(() => import('@/pages/CouponsPage'))
const ProfilePage = lazy(() => import('@/pages/ProfilePage'))
const ExamsPage = lazy(() => import('@/pages/exams'))
const CertificatesPage = lazy(() => import('@/pages/certificates'))
const ReportsPage = lazy(() => import('@/pages/reports'))
const NotificationsPage = lazy(() => import('@/pages/NotificationsPage'))
const AppUpdatesPage = lazy(() => import('@/pages/AppUpdatesPage'))
const VerifyCertificatePage = lazy(() => import('@/pages/verify/VerifyCertificatePage'))
const NotFound = lazy(() => import('@/pages/NotFound'))

const router = createBrowserRouter([
  // ── Public ─────────────────────────────────────────────────────────────────
  { path: '/login',           element: <Login /> },
  { path: '/forgot-password', element: <ForgotPassword /> },
  { path: '/unauthorized',    element: <UnauthorizedPage /> },
  {
    path: '/verify-certificate',
    element: (
      <Suspense fallback={<TablePageSkeleton />}>
        <VerifyCertificatePage />
      </Suspense>
    ),
  },
  {
    path: '/verify-certificate/:certificateNumber',
    element: (
      <Suspense fallback={<TablePageSkeleton />}>
        <VerifyCertificatePage />
      </Suspense>
    ),
  },
  {
    path: '/certificates/verify/:certificateNumber',
    element: (
      <Suspense fallback={<TablePageSkeleton />}>
        <VerifyCertificatePage />
      </Suspense>
    ),
  },
  {
    path: '/api/v1/certificates/verify/:certificateNumber',
    element: (
      <Suspense fallback={<TablePageSkeleton />}>
        <VerifyCertificatePage />
      </Suspense>
    ),
  },

  // ── Protected — must be authenticated ──────────────────────────────────────
  {
    path:    '/',
    element: <AuthGuard />,
    children: [
      // Redirect / → /dashboard
      { index: true, element: <Navigate to="/dashboard" replace /> },

      // Layout shell wraps ALL super-admin pages
      {
        element: <SuperAdminLayout />,
        children: [
          // Super Admin only routes
          {
            path:    'dashboard',
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<DashboardSkeleton />}>
                    <DashboardContainer />
                  </Suspense>
                ),
              },
            ],
          },
          {
            path:    'reports',
            element: <PermissionGuard permission="VIEW_REPORTS" />,
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<TablePageSkeleton />}>
                    <ReportsPage />
                  </Suspense>
                ),
              },
            ],
          },
          {
            path:    'institutions',
            element: <PermissionGuard permission="MANAGE_INSTITUTIONS" />,
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<TablePageSkeleton />}>
                    <InstitutionsPage />
                  </Suspense>
                ),
              },
            ],
          },
          {
            path:    'students',
            element: <PermissionGuard permission="MANAGE_STUDENTS" />,
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<TablePageSkeleton />}>
                    <StudentsPage />
                  </Suspense>
                ),
              },
              {
                path: '*',
                element: (
                  <Suspense fallback={<TablePageSkeleton />}>
                    <StudentsPage />
                  </Suspense>
                ),
              },
            ],
          },
          {
            path:    'migrations',
            element: <PermissionGuard permission="MANAGE_STUDENTS" />,
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<TablePageSkeleton />}>
                    <StudentsPage />
                  </Suspense>
                ),
              },
              {
                path: '*',
                element: (
                  <Suspense fallback={<TablePageSkeleton />}>
                    <StudentsPage />
                  </Suspense>
                ),
              },
            ],
          },
          {
            path:    'courses',
            element: <PermissionGuard permission="MANAGE_SYLLABUS_COURSES" />,
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<TablePageSkeleton />}>
                    <CoursesPage />
                  </Suspense>
                ),
              },
            ],
          },
          {
            path:    'exams',
            element: <PermissionGuard permission="MANAGE_EXAMS_RESULTS" />,
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<TablePageSkeleton />}>
                    <ExamsPage />
                  </Suspense>
                ),
              },
            ],
          },
          {
            path:    'certificates',
            element: <PermissionGuard permission="MANAGE_CERTIFICATES" />,
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<TablePageSkeleton />}>
                    <CertificatesPage />
                  </Suspense>
                ),
              },
            ],
          },
          {
            path:    'staff',
            element: <SuperAdminGuard />,
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<TablePageSkeleton />}>
                    <StaffPage />
                  </Suspense>
                ),
              },
            ],
          },
          {
            path:    'payments',
            element: <PermissionGuard permission="MANAGE_PAYMENTS" />,
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<TablePageSkeleton />}>
                    <PaymentsPage />
                  </Suspense>
                ),
              },
            ],
          },
          {
            path:    'commissions',
            element: <PermissionGuard permission="MANAGE_COMMISSIONS" />,
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<TablePageSkeleton />}>
                    <CommissionsPage />
                  </Suspense>
                ),
              },
            ],
          },
          {
            path:    'coupons',
            element: <PermissionGuard permission="MANAGE_COUPONS" />,
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<TablePageSkeleton />}>
                    <CouponsPage />
                  </Suspense>
                ),
              },
            ],
          },
          {
            path:    'notifications',
            element: <PermissionGuard permission="MANAGE_NOTIFICATIONS" />,
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<TablePageSkeleton />}>
                    <NotificationsPage />
                  </Suspense>
                ),
              },
            ],
          },
          {
            path:    'app-updates',
            element: <SuperAdminGuard />,
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<TablePageSkeleton />}>
                    <AppUpdatesPage />
                  </Suspense>
                ),
              },
            ],
          },
          {
            path:    'profile',
            element: (
              <Suspense fallback={<ProfileSkeleton />}>
                <ProfilePage />
              </Suspense>
            ),
          },
        ],
      },
    ],
  },
  {
    path: '*',
    element: (
      <Suspense fallback={<TablePageSkeleton />}>
        <NotFound />
      </Suspense>
    ),
  },
])

export default router
